import React from "react";
import { Edit, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Header, Field, IconBtn, Modal } from "@/pages/admin/AdminBlog";

const emptyForm = {
  name: "", category: "mutual-fund", description: "", highlight: "",
  return_rate: "", risk_level: "", min_investment: "", image: "", active: true,
};
const categories = ["mutual-fund", "sip", "bond", "stock", "forex", "gold", "travel"];

export default function AdminProducts() {
  const [items, setItems] = React.useState([]);
  const [form, setForm] = React.useState(emptyForm);
  const [editingId, setEditingId] = React.useState(null);
  const [show, setShow] = React.useState(false);

  const load = async () => setItems((await api.get("/admin/products")).data);
  React.useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editingId) await api.put(`/admin/products/${editingId}`, form);
      else await api.post("/admin/products", form);
      toast.success("Saved");
      setShow(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/admin/products/${id}`);
    load();
  };

  return (
    <div data-testid="admin-products">
      <Header title="Products" onAdd={() => { setForm(emptyForm); setEditingId(null); setShow(true); }} addLabel="New Product" />
      <div className="mt-8 card-luxury overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5">
            <tr className="text-left text-xs uppercase tracking-widest text-slate-400">
              <th className="px-5 py-3">Name</th><th className="px-5 py-3">Category</th>
              <th className="px-5 py-3">Return</th><th className="px-5 py-3">Status</th><th className="px-5 py-3 w-32"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {items.map((p) => (
              <tr key={p.id}>
                <td className="px-5 py-3 text-white">{p.name}</td>
                <td className="px-5 py-3"><Badge className="bg-sky-500/10 text-sky-300 border-sky-500/20 hover:bg-sky-500/15">{p.category}</Badge></td>
                <td className="px-5 py-3 text-emerald-400 text-xs">{p.return_rate || "—"}</td>
                <td className="px-5 py-3 text-xs">{p.active ? <span className="text-emerald-400">Active</span> : <span className="text-slate-500">Inactive</span>}</td>
                <td className="px-5 py-3 text-right">
                  <IconBtn onClick={() => { setForm({ ...p }); setEditingId(p.id); setShow(true); }}><Edit className="w-3.5 h-3.5" /></IconBtn>
                  <IconBtn onClick={() => remove(p.id)} variant="danger"><Trash2 className="w-3.5 h-3.5" /></IconBtn>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="px-5 py-10 text-center text-slate-500 text-sm">No products yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {show && (
        <Modal title={editingId ? "Edit product" : "New product"} onClose={() => setShow(false)}>
          <form onSubmit={save} className="space-y-4">
            <Field label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></Field>
            <Field label="Category">
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full bg-[#0B1221] border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-sky-500 outline-none">
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Description"><Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Return rate"><Input value={form.return_rate || ""} onChange={(e) => setForm({ ...form, return_rate: e.target.value })} /></Field>
              <Field label="Risk level"><Input value={form.risk_level || ""} onChange={(e) => setForm({ ...form, risk_level: e.target.value })} /></Field>
              <Field label="Minimum investment"><Input value={form.min_investment || ""} onChange={(e) => setForm({ ...form, min_investment: e.target.value })} /></Field>
              <Field label="Image URL"><Input value={form.image || ""} onChange={(e) => setForm({ ...form, image: e.target.value })} /></Field>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              <span className="text-slate-300">Active</span>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShow(false)} className="btn-ghost">Cancel</button>
              <button type="submit" className="btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
